import React, {useEffect, useState, useMemo} from 'react';
import Section from '../Section';
import {useAuth} from '../../helpers/AuthContext';
import Alert from "../Alert";
import ProductsTable from "./ProductsTable";
import ProductBulkEditor from "./ProductBulkEditor";
import ProductBulkShippingEditor from "./ProductBulkShippingEditor";
import ProductBulkDelete from './ProductBulkDelete';
import ProductQuickSort from "./ProductQuickSort";
import ProductTabs from "./ProductTabs";
import ProductWizardCreateButton from "./Product/ProductWizardCreateButton";
import MarketplaceVisibilityBanner from "./MarketplaceVisibilityBanner";

export default function ProductsListSection({ enableWizard = false }) {
    const {fetch, channel} = useAuth();
    const [success, setSuccess] = useState('');
    const [error, setError] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showBulkEditor, setShowBulkEditor] = useState(false);
    const [showBulkShipping, setShowBulkShipping] = useState(false);
    const [showBulkDelete, setShowBulkDelete] = useState(false);
    const [showQuickSort, setShowQuickSort] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const fetchProducts = async () => {
        try {
            const response = await fetch('/account/products');
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return await response.json();
        } catch (error) {
            console.error('Failed to fetch products:', error);
            return [];
        }
    };

    const loadProducts = async ({ showLoadingState = true } = {}) => {
        if (showLoadingState) {
            setLoading(true);
        } else {
            setIsRefreshing(true);
        }
        try {
            const productsData = await fetchProducts();
            setProducts(productsData);
        } catch (error) {
            console.error('Error loading products:', error);
        } finally {
            if (showLoadingState) {
                setLoading(false);
            } else {
                setIsRefreshing(false);
            }
        }
    };

    useEffect(() => {
        loadProducts();
    }, [fetch]);

    // Handle selection changes from table
    const handleSelectionChange = (selectedRows) => {
        setSelectedProducts(selectedRows);
    };

    // Handle bulk editor save
    const handleBulkEditorSave = (successMessage = '', errorMessage = '') => {
        if (successMessage) {
            setSuccess(successMessage);
        }
        if (errorMessage) {
            setError(errorMessage);
        }
        // Reload products to reflect changes
        loadProducts();
        // Clear selection
        setSelectedProducts([]);
    };

    // Handle quick sort save
    const handleQuickSortSave = (successMessage = '', errorMessage = '') => {
        if (successMessage) {
            setSuccess(successMessage);
        }
        if (errorMessage) {
            setError(errorMessage);
        }
        // Reload products to reflect changes
        loadProducts();
        // Clear selection
        setSelectedProducts([]);
    };

    // Calculate tab counts based on product status
    const calculateTabCounts = (products) => {
        const counts = {
            all: products.length,
            active: 0,
            sold: 0,
            draft: 0
        };

        products.forEach(product => {
            if (product.isAvailable) {
                counts.active++;
            } else {
                counts.draft++;
            }
            
            // A product is considered "sold" if it has paid orders
            if (product.paidOrdersCount > 0) {
                counts.sold++;
            }
        });

        return counts;
    };

    // Filter products based on active tab
    const filterProducts = (products, tab) => {
        switch (tab) {
            case 'active':
                return products.filter(product => product.isAvailable);
            case 'draft':
                return products.filter(product => !product.isAvailable);
            case 'sold':
                return products.filter(product => product.paidOrdersCount > 0);
            case 'all':
            default:
                return products;
        }
    };

    // Filter products based on search query
    const searchProducts = (products, query) => {
        if (!query.trim()) {
            return products;
        }
        const lowerQuery = query.toLowerCase();
        return products.filter(product => 
            product.name.toLowerCase().includes(lowerQuery)
        );
    };

    // Handle tab change
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        // Clear selection when switching tabs
        setSelectedProducts([]);
    };

    // Handle search query change
    const handleSearchChange = (query) => {
        setSearchQuery(query);
        // Clear selection when searching
        setSelectedProducts([]);
    };

    const handleRefresh = () => {
        setSelectedProducts([]);
        loadProducts({ showLoadingState: false });
    };

    // Get filtered products and tab counts
    const tabCounts = calculateTabCounts(products);
    // Memoize filtered products to prevent unnecessary re-renders and selection clearing
    const filteredProducts = useMemo(() => {
        const tabFiltered = filterProducts(products, activeTab);
        return searchProducts(tabFiltered, searchQuery);
    }, [products, activeTab, searchQuery]);

    // effect to clear success and error messages after a delay
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    return (
        <>
        <MarketplaceVisibilityBanner />
        <Section
            id="your-products"
            title="Products List"
            className="products-list-section"
            saveState={true}
            preview="Manage your products, edit details, and organize them for your store."
        >
            {loading ? (
                <div>Loading products...</div>
            ) : (
                <>
                    {success && <Alert type="success">{success}</Alert>}
                    {error && <Alert type="error">{error}</Alert>}

                    <p>The <strong>top four</strong> products are shown on <a target="_blank" href={`${channel?.url}/`}>your homepage <i className="fa-solid fa-arrow-up-right-from-square"></i></a> to
                        make room for your links. All products are shown <a target="_blank" href={`${channel?.url}/store`}>on your store page <i className="fa-solid fa-arrow-up-right-from-square"></i></a>.</p>

                    {products.length >= 10 && (<ProductTabs
                        activeTab={activeTab}
                        onTabChange={handleTabChange}
                        tabCounts={tabCounts}
                        onSearchChange={handleSearchChange}
                        searchQuery={searchQuery}
                        onRefresh={handleRefresh}
                        isRefreshing={isRefreshing}
                    />)}

                    {selectedProducts.length > 0 && (
                        <Alert type="info" className="bulk-actions">
                            <p>
                                {selectedProducts.length} products selected. <a
                                style={{cursor: "pointer"}}
                                className="bold underlined"
                                onClick={() => setShowBulkEditor(true)}
                            >Bulk Edit Selected</a> | <a
                                style={{cursor: "pointer"}}
                                className="bold underlined"
                                onClick={() => setShowBulkShipping(true)}
                            >Bulk Edit Shipping</a> | <a
                                style={{cursor: "pointer"}}
                                className="bold underlined"
                                onClick={() => setShowQuickSort(true)}
                            >Quick Sort Selected</a> | <a
                                style={{cursor: 'pointer'}}
                                className="bold underlined error"
                                onClick={() => setShowBulkDelete(true)}
                            >Delete Selected</a>.
                            </p>
                        </Alert>
                    )}

                    <ProductsTable
                        products={filteredProducts}
                        setProducts={setProducts}
                        setSuccess={setSuccess}
                        setError={setError}
                        channel={channel}
                        onSelectionChange={handleSelectionChange}
                        enableWizard={enableWizard}
                        onProductsChanged={loadProducts}
                    />

                    {showBulkEditor && (
                        <ProductBulkEditor
                            selectedProducts={selectedProducts}
                            allProducts={products}
                            onClose={() => setShowBulkEditor(false)}
                            onSave={handleBulkEditorSave}
                            channel={channel}
                        />
                    )}

                    {showBulkShipping && (
                        <ProductBulkShippingEditor
                            selectedProducts={selectedProducts}
                            allProducts={products}
                            onClose={() => setShowBulkShipping(false)}
                            onSave={handleBulkEditorSave}
                            channel={channel}
                        />
                    )}

                    {showBulkDelete && (
                        <ProductBulkDelete
                            selectedProducts={selectedProducts}
                            allProducts={products}
                            onClose={() => setShowBulkDelete(false)}
                            onSave={handleBulkEditorSave}
                            onError={setError}
                        />
                    )}

                    {showQuickSort && (
                        <ProductQuickSort
                            selectedProducts={selectedProducts}
                            allProducts={products}
                            onClose={() => setShowQuickSort(false)}
                            onSave={handleQuickSortSave}
                            channel={channel}
                        />
                    )}
                </>
            )}
        </Section>
        </>
    );

}
